import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from '@mui/material';
import { ChevronDown, MessageSquare, Plus, Search, Trash2 } from 'lucide-react';
import type { ChatListItem } from '../ChatInfoPanel';
import styles from './ChatSwitcher.module.scss';

interface ChatSwitcherProps {
  chats: ChatListItem[];
  activeChatId: string | null;
  isLoadingChats?: boolean;
  onSwitchChat: (id: string) => void;
  onCreateChat: (title: string) => Promise<void>;
  onDeleteChat: (id: string) => Promise<void>;
  setAnalysysMode: (value: boolean) => void;
  analysysMode: boolean;
}

const DEFAULT_TITLE = 'Новый чат';

const ChatSwitcher = ({
  chats,
  activeChatId,
  isLoadingChats = false,
  onSwitchChat,
  onCreateChat,
  onDeleteChat,
  setAnalysysMode,
  analysysMode,
}: ChatSwitcherProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const activeChat = chats.find((c) => c.id === activeChatId) ?? null;

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => c.label.toLowerCase().includes(q));
  }, [chats, search]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleSwitch = useCallback(
    (id: string) => {
      onSwitchChat(id);
      handleClose();
    },
    [onSwitchChat, handleClose]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await onDeleteChat(id);
    },
    [onDeleteChat]
  );

  const handleOpenNewDialog = useCallback(() => {
    setNewTitle('');
    setNewDialogOpen(true);
    setOpen(false);
  }, []);

  const handleCloseNewDialog = useCallback(() => {
    setNewDialogOpen(false);
    setNewTitle('');
  }, []);

  const handleConfirmNew = useCallback(async () => {
    const title = newTitle.trim();
    if (!title) return;
    await onCreateChat(title);
    handleCloseNewDialog();
  }, [newTitle, onCreateChat, handleCloseNewDialog]);

  return (
    <div className={styles.root}>
      <button
        ref={anchorRef}
        type="button"
        className={styles.titleButton}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <MessageSquare size={16} className={styles.titleIcon} aria-hidden />
        <span className={styles.titleLabel}>
          {activeChat?.label ?? (isLoadingChats ? 'Загрузка…' : 'Новый чат')}
        </span>
        {chats.length > 0 && <span className={styles.titleCount}>{chats.length}</span>}
        <ChevronDown
          size={16}
          className={`${styles.titleChevron} ${open ? styles.titleChevronOpen : ''}`}
          aria-hidden
        />
      </button>

      <Box display="flex">
        <Checkbox
          size="small"
          checked={analysysMode}
          onChange={(e) => setAnalysysMode(e.target.checked)}
        />
        <p>Глубокий анализ документов</p>
      </Box>

      <Tooltip title="Новый чат">
        <IconButton
          type="button"
          size="small"
          className={styles.newButton}
          onClick={handleOpenNewDialog}
          aria-label="Новый чат"
        >
          <Plus size={16} strokeWidth={2.25} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            className: styles.popoverPaper,
            elevation: 0,
          },
        }}
      >
        <div className={styles.popoverInner}>
          {chats.length > 5 && (
            <div className={styles.searchWrap}>
              <Search size={14} className={styles.searchIcon} aria-hidden />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск чатов"
                className={styles.searchInput}
                autoFocus
                aria-label="Поиск чатов"
              />
            </div>
          )}

          <div className={styles.list} role="listbox">
            {isLoadingChats ? (
              <div className={styles.empty}>Загрузка…</div>
            ) : filteredChats.length === 0 ? (
              <div className={styles.empty}>
                {chats.length === 0 ? 'Чатов пока нет' : 'Ничего не найдено'}
              </div>
            ) : (
              filteredChats.map((c) => {
                const isActive = c.id === activeChatId;
                return (
                  <div
                    key={c.id}
                    role="option"
                    tabIndex={0}
                    aria-selected={isActive}
                    className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                    onClick={() => handleSwitch(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSwitch(c.id);
                      }
                    }}
                  >
                    <MessageSquare size={14} className={styles.itemIcon} aria-hidden />
                    <span className={styles.itemLabel}>{c.label}</span>
                    {chats.length > 1 && (
                      <Tooltip title="Удалить чат" placement="right">
                        <IconButton
                          type="button"
                          size="small"
                          className={styles.itemDelete}
                          aria-label={`Удалить «${c.label}»`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(c.id);
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className={styles.popoverFooter}>
            <Button
              type="button"
              variant="contained"
              size="small"
              fullWidth
              disableElevation
              startIcon={<Plus size={14} strokeWidth={2.25} />}
              onClick={handleOpenNewDialog}
              sx={{ borderRadius: 1.25, py: 0.75 }}
            >
              Новый чат
            </Button>
          </div>
        </div>
      </Popover>

      <Dialog
        open={newDialogOpen}
        onClose={handleCloseNewDialog}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Новый чат</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название чата"
            fullWidth
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newTitle.trim()) {
                e.preventDefault();
                handleConfirmNew();
              }
            }}
            placeholder={`Например, ${DEFAULT_TITLE.toLowerCase()}`}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={handleCloseNewDialog}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="contained"
            disableElevation
            disabled={!newTitle.trim()}
            onClick={handleConfirmNew}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ChatSwitcher;
